import { useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { Turnstile } from "@/components/Turnstile";
import { authForgotPassword, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, MailCheck, ArrowLeft } from "lucide-react";

const field = "w-full border-2 border-zinc-300 focus:border-[#2F6DA3] focus:outline-none px-4 py-3 text-sm transition-colors";
const label = "block text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-1.5";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onVerify = useCallback((t) => setToken(t), []);
  const widget = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { setError("Please complete the anti-bot check below."); return; }
    setLoading(true); setError("");
    try {
      await authForgotPassword(email, token);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      widget.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        <div className="text-center" data-testid="forgot-success">
          <div className="inline-flex bg-emerald-50 border-2 border-emerald-200 p-4 mb-5"><MailCheck size={30} className="text-emerald-600" /></div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mb-2">Check your inbox</h1>
          <p className="text-sm text-zinc-500 mb-6">If an account exists for <strong>{email}</strong>, we've sent a link to reset your password. It expires in 1 hour.</p>
          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-bold text-[#2F6DA3] hover:underline"><ArrowLeft size={15} /> Back to login</Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Reset your password</h1>
      <p className="text-sm text-zinc-500 mb-8">Enter your email and we'll send you a reset link.</p>
      <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
        <div>
          <label className={label}>Email</label>
          <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={field} placeholder="you@company.co.uk" />
        </div>
        {error && <div data-testid="forgot-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
        <Turnstile ref={widget} action="forgot_password" onVerify={onVerify} />
        <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-[#2F6DA3] hover:bg-[#234E74] text-white font-bold uppercase tracking-wider py-6">
          {loading ? <RefreshCw size={16} className="mr-2 animate-spin" /> : null} Send reset link
        </Button>
      </form>
      <p className="text-sm text-zinc-500 mt-6 text-center">
        <Link to="/login" data-testid="to-login" className="inline-flex items-center gap-1.5 font-bold text-[#14304F] hover:underline"><ArrowLeft size={14} /> Back to login</Link>
      </p>
    </AuthLayout>
  );
}
